import { ImprovementGoal } from "../../types";
import { goalProgress } from "../Dashboard/insights";

const WEEK = 7 * 24 * 3600 * 1000;

export type PhaseKey = "planning" | "execution" | "conclusion";
export type PhaseState = "done" | "active" | "upcoming";

export type GoalPhase = {
  key: PhaseKey;
  from: string;
  to: string;
  state: PhaseState;
};

/**
 * The three phases of a goal, with the same boundaries as findGoalStatus on the server:
 * planning is the first week, conclusion the last week and execution everything between.
 */
export const goalPhases = (goal: Pick<ImprovementGoal, "registered" | "endDate">, now = new Date()) => {
  const { progress, phase } = goalProgress(goal, now);
  const end = new Date(goal.endDate).getTime();
  const start = goal.registered ? new Date(goal.registered).getTime() : now.getTime();
  const planningEnd = Math.min(start + WEEK, end);
  const conclusionStart = Math.max(end - WEEK, planningEnd);
  const iso = (time: number) => new Date(time).toISOString();

  const order: PhaseKey[] = ["planning", "execution", "conclusion"];
  const current = phase === "finished" ? order.length : order.indexOf(phase);
  const stateOf = (index: number): PhaseState => (index < current ? "done" : index === current ? "active" : "upcoming");

  const phases: GoalPhase[] = [
    { key: "planning", from: iso(start), to: iso(planningEnd), state: stateOf(0) },
    { key: "execution", from: iso(planningEnd), to: iso(conclusionStart), state: stateOf(1) },
    { key: "conclusion", from: iso(conclusionStart), to: iso(end), state: stateOf(2) },
  ];

  // The conclusion opens when the end date is less than a week away.
  const conclusionOpen = end < now.getTime() + WEEK;

  return { phases, progress, conclusionOpen };
};
